import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import CumulativeSalesChart from "../../../MarketingInsights/components/SalesPerformance/CumulativeSalesChart";

/* ponytail: data-viz — prototype blue for both the solid actual line and dashed forecast. */
const ACTUAL_COLOR = "#1982C4";
const FORECAST_COLOR = "#1982C4";

const DAY_MS = 86400000;
const RANGES = [
  { key: "7", label: "Last 7 Days", days: 7 },
  { key: "30", label: "Last 30 Days", days: 30 },
  { key: "all", label: "All Time", days: null },
];

const parseShortDate = (mdy) => {
  if (!mdy) return 0;
  const [m, d, y] = mdy.split("/").map(Number);
  return new Date(y < 100 ? 2000 + y : y, m - 1, d).getTime();
};

const SalesPerformanceCard = ({
  ticketsSold, capacity, sellThroughPctExact, forecastAttendance, salesSeries, forecastCurve, forecastRationale, isPast,
}) => {
  const [range, setRange] = useState("all");
  const series = salesSeries || [];

  const filteredSeries = useMemo(() => {
    const days = RANGES.find((r) => r.key === range)?.days;
    if (!days || series.length === 0) return series;
    const cutoff = parseShortDate(series[series.length - 1].date) - days * DAY_MS;
    return series.filter((p) => parseShortDate(p.date) >= cutoff);
  }, [series, range]);

  // "This week" momentum is measured against the actual current date (today − 7d),
  // not the last point in the series — so for a past event, where the series ended
  // near the show date, the delta naturally resolves to 0 and the line is hidden.
  const weeklyDelta = useMemo(() => {
    if (series.length === 0) return null;
    const last = series[series.length - 1];
    const cutoff = Date.now() - 7 * DAY_MS;
    const prior = [...series].reverse().find((p) => parseShortDate(p.date) <= cutoff);
    return (last.number_of_tickets_sold || 0) - (prior ? prior.number_of_tickets_sold || 0 : 0);
  }, [series]);

  // Tickets Sold and Sell-Through track the selected range. For a window they
  // reflect the tickets sold across the visible (filtered) series — the growth
  // the chart shows — so the KPIs and chart stay in sync. "All Time" keeps the
  // authoritative full totals from props. Capacity and Forecast are totals and
  // never change with the range.
  const windowMetrics = useMemo(() => {
    if (range === "all" || filteredSeries.length === 0) {
      return { sold: ticketsSold, sellThrough: sellThroughPctExact };
    }
    const first = filteredSeries[0].number_of_tickets_sold || 0;
    const last = filteredSeries[filteredSeries.length - 1].number_of_tickets_sold || 0;
    const sold = last - first;
    const sellThrough = capacity > 0 ? Math.round((sold * 100 / capacity) * 10) / 10 : 0;
    return { sold, sellThrough };
  }, [range, filteredSeries, ticketsSold, sellThroughPctExact, capacity]);

  const projectedPct = capacity > 0 ? Math.round((forecastAttendance / capacity) * 100) : null;

  return (
    <div className="d2-card">
      <div className="d2-card-header">
        <span style={{ fontSize: "18px", fontWeight: 600, color: "var(--sl-text-primary)" }}>
          Sales Performance
        </span>
        <div className="btn-group">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={range === r.key ? "active" : ""}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="d2-kpi-grid">
        <div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Tickets Sold</div>
          <div style={{ fontSize: "32px", fontWeight: 700, lineHeight: 1.1 }}>{windowMetrics.sold.toLocaleString()}</div>
          {range === "all" && !isPast && weeklyDelta > 0 && (
            <div style={{ fontSize: "12px", color: "var(--sl-text-success)", fontWeight: 600, marginTop: "2px" }}>
              +{weeklyDelta.toLocaleString()} this week
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Capacity</div>
          <div style={{ fontSize: "32px", fontWeight: 700, lineHeight: 1.1 }}>{capacity.toLocaleString()}</div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginTop: "2px" }}>Total capacity</div>
        </div>
        <div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Sell-Through</div>
          <div style={{ fontSize: "32px", fontWeight: 700, lineHeight: 1.1 }}>{windowMetrics.sellThrough}%</div>
          {projectedPct != null && (
            <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginTop: "2px" }}>{projectedPct}% projected</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginBottom: "2px" }}>Forecast</div>
          <div style={{ fontSize: "32px", fontWeight: 700, lineHeight: 1.1 }}>{forecastAttendance.toLocaleString()}</div>
          <div style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", marginTop: "2px" }}>Internal Forecast</div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "12px", fontSize: "12px", color: "var(--sl-text-secondary)" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ display: "inline-block", width: "20px", height: "2px", background: ACTUAL_COLOR }} />
          Actual Sales
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ display: "inline-block", width: "20px", height: 0, borderTop: `2px dashed ${FORECAST_COLOR}` }} />
          Forecast
        </span>
      </div>

      <CumulativeSalesChart
        data={filteredSeries}
        forecastCurve={forecastCurve}
        capacity={capacity}
        actualColor={ACTUAL_COLOR}
        forecastColor={FORECAST_COLOR}
        capacityLabel={false}
      />
      {forecastRationale && !isPast && (
        <div style={{ marginTop: "12px", fontSize: "12px", lineHeight: 1.5, color: "var(--sl-text-tertiary)" }}>
          {forecastRationale}
        </div>
      )}
    </div>
  );
};

SalesPerformanceCard.propTypes = {
  ticketsSold: PropTypes.number.isRequired,
  capacity: PropTypes.number.isRequired,
  sellThroughPctExact: PropTypes.number.isRequired,
  forecastAttendance: PropTypes.number.isRequired,
  salesSeries: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    number_of_tickets_sold: PropTypes.number,
  })),
  forecastCurve: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    forecast: PropTypes.number,
  })),
  forecastRationale: PropTypes.string,
  isPast: PropTypes.bool,
};

SalesPerformanceCard.defaultProps = {
  salesSeries: [],
  forecastCurve: [],
  forecastRationale: null,
};

export default SalesPerformanceCard;
